// Spatial API (Plan 4b Task 5): the layer registry, vector tiles, the overlap check (Sobreposição), Oportunidades.
import { z } from 'zod';
import { Id, IsoDate } from './accounts.ts';

export const SpatialLayerView = z.object({
  id: z.string(),
  label: z.string(),
  source: z.string(),
  role: z.enum(['COMPETING_RIGHTS', 'RESTRICTION', 'OPPORTUNITY', 'REFERENCE']),
  severity: z.enum(['blocking', 'warning', 'info']),
  minZoom: z.number().int(),
  /** false: in the spec's registry but not ingested yet — the layer control shows it as coming. */
  available: z.boolean(),
  /** The module it needs (e.g. module.ambiental for IBAMA embargos), else null. */
  module: z.string().nullable(),
  /** The viewer lacks that module: listed, not drawn, not checked. */
  locked: z.boolean(),
});
export type SpatialLayerView = z.infer<typeof SpatialLayerView>;
export const SpatialLayersResponse = z.object({ layers: z.array(SpatialLayerView) });

export const TileParams = z.object({
  layer: z.string().regex(/^[a-z_]+$/),
  z: z.coerce.number().int(),
  x: z.coerce.number().int(),
  y: z.coerce.number().int(),
});

const Position = z.tuple([z.number(), z.number()]).rest(z.number());
const Ring = z.array(Position);
const GeoJsonPolygon = z.object({ type: z.literal('Polygon'), coordinates: z.array(Ring) });
const GeoJsonMultiPolygon = z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(Ring)) });

/** A drawn/uploaded area. SIRGAS 2000 (EPSG:4674) or WGS 84 (4326) — the same to within a metre for a screening. */
export const SobreposicaoBody = z.discriminatedUnion('format', [
  z.object({
    format: z.literal('geojson'),
    crs: z.enum(['EPSG:4674', 'EPSG:4326']).default('EPSG:4674'),
    geometry: z.union([GeoJsonPolygon, GeoJsonMultiPolygon]),
  }),
  z.object({ format: z.literal('kml'), kml: z.string().min(1).max(1_000_000) }),
  z.object({ format: z.literal('memorial'), text: z.string().min(1).max(200_000) }),
]);

export const OverlapFindingView = z.object({
  layer: z.string(),
  severity: z.enum(['blocking', 'warning', 'info']),
  processId: Id,
  processo: z.string().nullable(),
  fase: z.string().nullable(),
  titular: z.string().nullable(),
  overlapHa: z.number(),
});
export type OverlapFindingView = z.infer<typeof OverlapFindingView>;

/** A restriction area (IBAMA embargo, TI, UC…) over an area or a process (Plan 6 Task 6). */
export const RestrictionFindingView = z.object({
  layer: z.string(),
  severity: z.enum(['blocking', 'warning', 'info']),
  name: z.string().nullable(),
  label: z.string().nullable(),
  issuedOn: IsoDate.nullable(),
  info: z.record(z.string(), z.string().nullable()),
  overlapHa: z.number(),
});
export type RestrictionFindingView = z.infer<typeof RestrictionFindingView>;

/** A certified land parcel under an area or a process (Plan 6 Task 6c): reference data, never a conflict. The cartório
 *  (CNS) and matrícula are the route to the owner's name — anyone may request the certidão. */
export const ParcelFindingView = z.object({
  source: z.string(),
  parcelCode: z.string(),
  sncrCode: z.string().nullable(),
  name: z.string().nullable(),
  natureza: z.string().nullable(),
  status: z.string().nullable(),
  areaHa: z.number().nullable(),
  cns: z.string().nullable(),
  matricula: z.string().nullable(),
  registeredOn: IsoDate.nullable(),
  municipioIbge: z.number().int().nullable(),
  overlapHa: z.number(),
});
export type ParcelFindingView = z.infer<typeof ParcelFindingView>;

/** A rural property declared in the CAR over an area or a process (Plan 6 Task 6c, read on demand from SFB):
 *  self-declared, never ownership; no owner names are published. */
export const CarFindingView = z.object({
  code: z.string(),
  status: z.string().nullable(),
  type: z.string().nullable(),
  condition: z.string().nullable(),
  areaHa: z.number().nullable(),
  municipality: z.string().nullable(),
  createdOn: IsoDate.nullable(),
  overlapHa: z.number(),
});
export type CarFindingView = z.infer<typeof CarFindingView>;
/** `unavailable`: SFB's server did not answer — the page says so instead of failing. */
export const CarResultView = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), fetchedAt: z.string(), truncated: z.boolean(), properties: z.array(CarFindingView) }),
  z.object({ status: z.literal('unavailable') }),
]);
export type CarResultView = z.infer<typeof CarResultView>;

/**
 * `summary` (anyone without the Território module): how many conflicts, nothing else. `full`: the report — findings
 * and the free area, the number the user acts on (spec §9).
 */
export const SobreposicaoResponse = z.discriminatedUnion('level', [
  z.object({ level: z.literal('summary'), conflicts: z.number().int().nonnegative() }),
  z.object({
    level: z.literal('full'),
    conflicts: z.number().int().nonnegative(),
    consultedHa: z.number(),
    conflictingHa: z.number(),
    freeHa: z.number(),
    findings: z.array(OverlapFindingView),
    restrictions: z.array(RestrictionFindingView),
    /** Certified parcels under the area (the SIGEF layer, Território) — empty when the layer is not open. */
    parcels: z.array(ParcelFindingView),
    /** CAR properties under the area (Ambiental) — null when the layer is not open. */
    car: CarResultView.nullable(),
    /** Registry layers not checked (not ingested yet, or the viewer lacks their module) — the report says what it could not see. */
    notChecked: z.array(z.string()),
  }),
]);

/** Why an area was not checked (422): `invalid_geometry` + a reason, or `not_supported` (memorial). */
export const SpatialError = z.object({ error: z.string(), reason: z.string().optional() });

export const OportunidadesQuery = z.object({
  uf: z.string().regex(/^[A-Z]{2}$/).optional(),
  cursor: Id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const OportunidadesResponse = z.object({
  items: z.array(z.object({
    processId: Id,
    processo: z.string().nullable(),
    fase: z.string().nullable(),
    uf: z.string().nullable(),
    areaHa: z.number().nullable(),
    titular: z.string().nullable(),
    lastEventOn: IsoDate.nullable(),
    /** Its current substances, as ANM names them. */
    substances: z.array(z.string()),
  })),
  nextCursor: Id.nullable(),
});
export type OportunidadesResponse = z.infer<typeof OportunidadesResponse>;

/** Oportunidades near the portfolio: the areas in disponibilidade near the titulares' processes, same or similar material (P93). */
export const OportunidadesCarteiraQuery = z.object({
  anmEntity: z.string().optional(),
  raioKm: z.coerce.number().int().refine((n) => [10, 25, 50, 100].includes(n)).default(50),
  /** 'igual': the same substance only; 'parecida': or one of its family. */
  material: z.enum(['igual', 'parecida']).default('parecida'),
  uf: z.string().regex(/^[A-Z]{2}$/).optional(),
  /** Only the areas matching on these substances (the area's own, as ANM names it); repeat the key for several. */
  substancia: z.preprocess((v) => (v == null || Array.isArray(v) ? v : [v]), z.array(z.string().min(1).max(80)).max(50)).optional(),
});
export const NearbyOpportunity = OportunidadesResponse.shape.items.element.extend({
  match: z.enum(['same', 'family']),
  /** The area's substance that matched, the titular's own it matched, and that one's family label. */
  substance: z.string(),
  ownSubstance: z.string(),
  family: z.string().nullable(),
  near: z.object({ processId: Id, processo: z.string().nullable(), distanceKm: z.number() }),
});
export const OportunidadesCarteiraResponse = z.object({
  items: z.array(NearbyOpportunity),
  /** The list stopped at its limit (the closest kept): narrow the radius or the material. */
  truncated: z.boolean(),
  /** How many processes the titulares hold (the ones searched around: those active, with a polygon and a substance). */
  searched: z.number().int(),
  /** The substances the areas near the portfolio match on, and in how many areas — before the substance filter. */
  substances: z.array(z.object({ name: z.string(), count: z.number().int() })),
  /** The polygons: the areas listed and the titulares' processes they are near. */
  shapes: z.array(z.object({ id: Id, canonicalRef: z.string().nullable(), own: z.boolean(), type: z.literal('MultiPolygon'), coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))) })),
});
export type OportunidadesCarteiraQuery = z.infer<typeof OportunidadesCarteiraQuery>;
export type NearbyOpportunity = z.infer<typeof NearbyOpportunity>;
export type OportunidadesCarteiraResponse = z.infer<typeof OportunidadesCarteiraResponse>;
